import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { Alert, ConfigProvider, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';
import { Colors, NetworkErrors, UserRole } from 'context';
import { Button, ButtonSizes, ToastTypes } from 'atoms';
import {
  appState,
  authState,
  DiplomaLanguageEnum,
  useDiplomaDownloadUrl,
  useMyDiplomas,
  useToaster,
} from 'core';
import type { IMyDiploma } from 'core';

const TOAST_KEY = 'diploma_download';

/**
 * "Мои дипломы" — the student's own diploma CERTIFICATES (the PL/EN PDFs staff
 * attach in the ERP). Distinct from the `graduationProject` / дипломный проект
 * of the lesson tabs, which is coursework.
 *
 * A diploma is visible as soon as it is attached: there is deliberately no
 * group-completion gate and no debt gate (spec 0016).
 */
export const Diplomas = memo(() => {
  const { t } = useTranslation();

  const { active_role, isDemoUser } = useReactiveVar(authState);
  const currentState = appState();

  const { diplomas, loading, error } = useMyDiplomas();
  const { getDownloadUrl, loadingId } = useDiplomaDownloadUrl();
  const { addToast } = useToaster();

  useEffect(() => {
    if (active_role !== UserRole.STUDENT || isDemoUser) {
      appState({
        ...currentState,
        networkError: NetworkErrors.pageIsNotAvailable,
      });
    }
  }, [active_role, currentState, isDemoUser]);

  // The link is minted per click and is short-lived by design — never prefetched
  // into the list. Opened in a new tab so the LMS tab is left untouched (the
  // presigned S3 GET has no attachment disposition, so the PDF renders inline).
  const downloadingRef = useRef(false);

  const onDownload = useCallback(
    async (diploma: IMyDiploma) => {
      // Re-entrance guard. The button is also `disabled` while loading, but that
      // only takes effect after a re-render; this does not. Without it two fast
      // clicks mint two presigned URLs and write two `diploma_file_url_issued`
      // audit rows for a single intent.
      if (downloadingRef.current) {
        return;
      }
      downloadingRef.current = true;

      // Opened SYNCHRONOUSLY, before the await — same as `LessonZoomJoin` and
      // the ERP diplomas tab. Browsers (Safari especially) only treat
      // `window.open` as user-initiated inside the click handler's own task, so
      // opening it after the round-trip is reliably vetoed by the popup blocker.
      // We then point the already-granted tab at the URL once it resolves.
      const tab = window.open('', '_blank');
      if (tab) {
        // Restores the `noopener` guarantee we give up by needing a handle on
        // the tab: about:blank is still same-origin here, and the disowning
        // survives the navigation to S3.
        tab.opener = null;
      }

      try {
        const url = await getDownloadUrl(diploma.id);
        if (!url) {
          tab?.close();
          addToast(TOAST_KEY, {
            type: ToastTypes.danger,
            text: `${t('diplomas.downloadError')}`,
          });
          return;
        }
        if (tab) {
          tab.location.href = url;
        } else {
          // Blocked even though the open was synchronous (e.g. Safari's "block
          // all pop-ups") — surface that instead of letting the click look like
          // a no-op.
          addToast(TOAST_KEY, {
            type: ToastTypes.warning,
            text: `${t('diplomas.popupBlocked')}`,
          });
        }
      } catch {
        tab?.close();
        addToast(TOAST_KEY, {
          type: ToastTypes.danger,
          text: `${t('diplomas.downloadError')}`,
        });
      } finally {
        downloadingRef.current = false;
      }
    },
    [getDownloadUrl, addToast, t],
  );

  const formatSize = useCallback(
    (bytes: number | null): string => {
      if (!bytes || bytes <= 0) {
        return '';
      }
      const mb = bytes / (1024 * 1024);
      return mb >= 1
        ? `${mb.toFixed(1)} ${t('diplomas.size.mb')}`
        : `${Math.max(1, Math.round(bytes / 1024))} ${t('diplomas.size.kb')}`;
    },
    [t],
  );

  const columns: ColumnsType<IMyDiploma> = useMemo(
    () => [
      {
        title: t('diplomas.columns.document'),
        key: 'document',
        render: (_, row) => (
          <DocumentCell>
            <LanguageTag color={row.language === DiplomaLanguageEnum.Pl ? 'blue' : 'geekblue'}>
              {t(`diplomas.languages.${row.language}`, { defaultValue: row.language })}
            </LanguageTag>
            {row.originalName ? <FileName>{row.originalName}</FileName> : null}
          </DocumentCell>
        ),
      },
      {
        title: t('diplomas.columns.group'),
        key: 'group',
        render: (_, row) => row.groupName || '—',
      },
      {
        title: t('diplomas.columns.course'),
        key: 'course',
        render: (_, row) => row.courseName || '—',
      },
      {
        title: t('diplomas.columns.issuedAt'),
        key: 'issuedAt',
        render: (_, row) =>
          row.issuedAt ? DateTime.fromISO(row.issuedAt).toFormat('dd.MM.yyyy') : '—',
      },
      {
        title: t('diplomas.columns.size'),
        key: 'size',
        render: (_, row) => formatSize(row.sizeBytes) || '—',
      },
      {
        title: '',
        key: 'action',
        render: (_, row) => (
          <DownloadButton
            size={ButtonSizes.sm}
            variant="outline"
            isLoading={loadingId === row.id}
            // Every row is disabled while any mint is in flight: `loadingId` is
            // a single slot, so letting a second row start would hand the
            // spinner to the wrong button. The clicked row shows the spinner.
            disabled={loadingId !== null}
            onClick={() => onDownload(row)}>
            {t('diplomas.download')}
          </DownloadButton>
        ),
      },
    ],
    [t, formatSize, loadingId, onDownload],
  );

  return (
    <DiplomasPageWrapper>
      <DiplomasHeader>
        <PageTitle>{t('diplomas.title')}</PageTitle>
        <PageInfo>{t('diplomas.info')}</PageInfo>
      </DiplomasHeader>

      {error ? (
        <Alert type="error" showIcon message={t('diplomas.loadError')} />
      ) : (
        <ConfigProvider theme={{ token: { colorPrimary: `${Colors.main}` } }}>
          <StyledTable<IMyDiploma>
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={diplomas}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{
              // Not an error state: a student simply has no diploma until one is
              // issued, so the copy stays neutral and forward-looking.
              emptyText: (
                <EmptyState>
                  <EmptyTitle>{t('diplomas.empty.title')}</EmptyTitle>
                  <EmptyHint>{t('diplomas.empty.hint')}</EmptyHint>
                </EmptyState>
              ),
            }}
          />
        </ConfigProvider>
      )}
    </DiplomasPageWrapper>
  );
});

const DiplomasPageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const DiplomasHeader = styled.div`
  margin: 32px 0;

  ${tablet} {
    margin: 40px 0 32px 0;
  }

  ${desktop} {
    margin: 54px 0 32px 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  ${tablet} {
    ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  }

  ${desktop} {
    ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.regular })}
  }
`;

const DocumentCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const LanguageTag = styled(Tag)`
  margin-inline-end: 0;
`;

const FileName = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
  word-break: break-all;
`;

const DownloadButton = styled(Button)`
  width: auto;

  ${mobile} {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 24px 0;
`;

const EmptyTitle = styled.span`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.medium })}
  color: ${Colors.black};
`;

const EmptyHint = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

// "&&" doubles the styled-components class so every rule beats antd v5's own
// cssinjs selectors (which are prefixed with .ant-table-wrapper) regardless of
// style-tag injection order.
const StyledTable = styled(Table)`
  && .ant-table-thead > tr > th {
    background: transparent;
    color: ${Colors.gray_dark};
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.4px;
    border-bottom: 2px solid ${Colors.additional_gray};
  }

  /* antd draws a vertical column separator as th::before with a heavily
     :not()-qualified selector — !important is the sane way to out-rank it. */
  && .ant-table-thead > tr > th::before {
    display: none !important;
  }

  && .ant-table-tbody > tr > td {
    padding: 16px;
    border-bottom: 1px solid ${Colors.additional_gray};
  }

  .ant-tag {
    border-radius: 999px;
    padding: 2px 12px;
    border: none;
    font-weight: 500;
  }
` as typeof Table;
